import { Checkbox, FormControlLabel } from "@mui/material";
import { FormikProps } from "formik";
import { useSearchParams } from "react-router-dom";
import CustomTextArea from "../../../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../../../components/Text";
import useDisclosure from "../../../../../../../../../../../hooks/use-disclosure";
import { useRemoveClinicalFromEncounter } from "../../../../../../../../../../../queries/encounter/use-remove-clinical-from-encounter";
import { usePatientMedicationList } from "../../../../../../../../../../../queries/medication/use-patient-medication-list";
import { RemoveClinicalDataFromEncounterTypeEnum } from "../../../../../../../../../../../services";
import {
  buildEntryLine,
  MEDICATION_SECTION,
} from "../../../../../../../../../../../utils/subjective-entry";
import {
  DEF_PAGINATION,
  getXtenantId,
} from "../../../../../../../../../../../utils/utils";
import { StyleAddButton } from "../../../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import { Form2Payload } from "../../../../../../../../scheduling/appointment/encounter/encounter-constant";
import UpsertMedicationsDrawer from "../../../../../medication/UpsertMedicationsDrawer";
import PreviewContentWrapper from "../PreviewContentWrapper";

interface ProgressNoteMedicationsProps {
  formik: FormikProps<Form2Payload>;
}

// The medication list query types its content as OptionData even though the
// endpoint returns full medication records, so the fields this section reads are
// named here.
type PatientMedication = {
  uuid?: string;
  archive?: boolean;
  medicalCodes?: Record<string, string>;
  prescribedBy?: Record<string, string>;
};

/**
 * Medications — the "not currently taking any" flag sits on top and drives the
 * rest of the section: unchecked (the default) the provider gets the add button
 * with the medications linked to this visit listed under it; checked, both are
 * hidden and the note carries the flag instead.
 *
 * Checking the flag never deletes anything — the medications stay on the
 * patient's chart, and the report skips the list while the flag is set. Only
 * the delete action on a listed row unlinks a record, and even then only from
 * this encounter.
 *
 * Both medications and supplements are captured by the same medication drawer
 * and list the rest of the chart uses, so this section reuses them rather than
 * keeping its own copy on the note.
 */
const ProgressNoteMedications = ({ formik }: ProgressNoteMedicationsProps) => {
  const medicationDisclosure = useDisclosure();
  const appointmentid = useSearchParams()[0].get("appointmentid");
  const { mutateAsync: removeClinicalFromEncounter } =
    useRemoveClinicalFromEncounter();

  const noMedications = !!formik.values?.noCurrentMedications;

  const { data: medications, refetch: refetchMedications } =
    usePatientMedicationList({
      archive: false,
      pagination: DEF_PAGINATION,
      appointmentUuid: appointmentid ?? undefined,
    });

  const linkedMedications = (
    (medications?.data?.content ?? []) as PatientMedication[]
  ).filter((medication) => !medication?.archive);

  /**
   * Unlinks the medication from this encounter only — the record stays in the
   * patient's chart.
   */
  const removeFromNote = (uuid?: string) =>
    removeClinicalFromEncounter({
      type: RemoveClinicalDataFromEncounterTypeEnum.Medication,
      appointmentUuid: appointmentid ?? "",
      recordUuid: uuid?.toString() ?? "",
      xTENANTID: getXtenantId(),
    }).then((res) => {
      refetchMedications();
      return res;
    });

  return (
    <Flex flexDirection="column" gap={1} width="100%">
      <FormControlLabel
        sx={{ mr: 0 }}
        control={
          <Checkbox
            sx={{ py: 0.25 }}
            name="noCurrentMedications"
            checked={noMedications}
            onChange={(_, checked) =>
              formik.setFieldValue("noCurrentMedications", checked)
            }
          />
        }
        label={
          <Text fontSize="14px">
            Patient is not currently taking any medications or supplements.
          </Text>
        }
      />

      {!noMedications && (
        <>
          <Text fontSize="14px" fontWeight="bold">
            Medication
          </Text>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => medicationDisclosure.open()}
            label="Add Medication or Supplement"
          />

          {!!linkedMedications.length && (
            <PreviewContentWrapper
              dataList={linkedMedications}
              k="medication"
              renderContent={(medication) => (
                <Text sx={{ color: "#424242" }}>
                  {buildEntryLine(MEDICATION_SECTION, medication)}
                </Text>
              )}
              onDelete={(uuid) => {
                removeFromNote(uuid);
              }}
              onEdit={(medication) => {
                medicationDisclosure.open({
                  ...medication,
                  medicalCodes: medication?.medicalCodes
                    ? Object.keys(medication?.medicalCodes)[0]
                    : undefined,
                  prescribedBy: medication?.prescribedBy
                    ? {
                        value: Object.keys(medication?.prescribedBy)[0],
                        label: Object.values(medication?.prescribedBy)[0],
                      }
                    : undefined,
                });
              }}
            />
          )}

          <UpsertMedicationsDrawer disclosureHost={medicationDisclosure} />
        </>
      )}

      <Text fontSize="14px" fontWeight="bold" mt={1}>
        Medication Comments
      </Text>
      <CustomTextArea
        rows={3}
        name="medicationComments"
        placeholder="Comments on medications, adherence, or side effects"
        onBlur={formik.handleBlur}
        value={formik.values?.medicationComments || ""}
        onChange={formik.handleChange}
      />
    </Flex>
  );
};

export default ProgressNoteMedications;
